import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

interface Subcategory {
  id: string;
  name: string;
  categoryId: string;
}

interface Category {
  id: string;
  name: string;
  subcategories: Subcategory[];
}

interface CategoryDropdownProps {
  category: Category;
}

export default function CategoryDropdown({ category }: CategoryDropdownProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Close dropdown when tapping outside (touch devices)
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 100);
  };

  const handleCategoryClick = (e: React.MouseEvent) => {
    // On touch devices, first tap opens the dropdown, second tap navigates
    if (hasSubcategories && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
      return;
    }
    navigate(`/category/${category.id}`);
    setIsOpen(false);
  };

  const handleSubcategoryClick = (subcategoryId: string) => {
    navigate(`/category/${category.id}/${subcategoryId}`);
    setIsOpen(false);
  };

  const hasSubcategories = category.subcategories && category.subcategories.length > 0;

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={dropdownRef}
    >
      <button
        onClick={handleCategoryClick}
        className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-neutral-900 hover:text-brand-primary transition-colors duration-200"
        aria-expanded={isOpen}
        aria-haspopup={hasSubcategories}
      >
        {category.name}
        {hasSubcategories && (
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-300 ease-out ${
              isOpen ? 'rotate-180' : 'rotate-0'
            }`}
          />
        )}
      </button>

      {hasSubcategories && (
        <div
          className={`absolute left-0 top-full mt-1 w-56 bg-white rounded-md shadow-lg border border-neutral-200 py-2 origin-top ${
            isOpen
              ? 'opacity-100 scale-y-100 translate-y-0 pointer-events-auto z-[1100] transition-all duration-200 ease-out'
              : 'opacity-0 scale-y-95 -translate-y-2 pointer-events-none z-[1100] transition-all duration-200 ease-in'
          }`}
        >
          {category.subcategories.map((subcategory) => (
            <button
              key={subcategory.id}
              onClick={() => handleSubcategoryClick(subcategory.id)}
              className="w-full text-left px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-100 hover:text-brand-primary transition-all duration-200 hover:pl-5"
            >
              {subcategory.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
